// src/pages/Teams.jsx
// Users → Teams (3.19.0): teams inside a business client (individual
// clients have none, though they may have several numbers). 3.19.6: a fax
// number belongs to at most one team; a new team needs its own number. Each team has a name, ONE
// of the client's fax numbers (one team per number since 3.19.6: the server refuses a number another team has), optional
// To/Cc/Bcc for its incoming faxes, and its members; each member has a
// default team (the number they send from by email). Who sees a team's
// incoming faxes: its members and the client's Admins (lib/accounts.js on
// the backend). Super Admins, and roles with "Manage teams".
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { apiCall } from '../components/EmailEditorParts';
import SortTh, { useSort } from '../components/SortTh';
import { SECTION_TITLE, SECTION_SUB } from '../theme/tabs';
import Loader from '../components/Loader';
import { useClientWord } from '../hooks/useClientWord';

const fmtFax = n => { const d = String(n || '').replace(/\D/g, '').slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n || '—'; };
const toList = v => String(v || '').split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
const toText = v => [].concat(v || []).join(', ');
const EMPTY = { name: '', number: '', incoming: '', incomingCc: '', incomingBcc: '', alsoClient: false };

export default function Teams() {
  // 3.67.5: the people at a client read 'your company'; Super Admins 'the client'
  const atCo = useClientWord() === 'Client' ? 'the client' : 'your company';
  const { getToken, effectiveInfo, refreshInfo } = useAuth();
  const { notify, confirm } = useNotify();
  const info = effectiveInfo || {};
  const allClients = info.level === 'super_admin' && (!info.accountId || info.accountId === 'all');
  const business = (info.accounts || []).filter(a => a.type !== 'individual');
  const current = (info.accounts || []).find(a => a.id === info.accountId);
  // Super Admins on All clients pick one here (3.19.10: or it comes from a
  // Control Panel → Clients link, ?client=<id>)
  const [client, setClient] = useState(() => { try { return new URLSearchParams(window.location.search).get('client') || ''; } catch { return ''; } });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const q = allClients ? `?account=${encodeURIComponent(client)}` : '';
  const call = useCallback((path, opts) => apiCall(`/portal/teams${path}${q}`, getToken, opts), [getToken, q]);

  const load = useCallback(async (changed = false) => {
    setError('');
    if (allClients && !client) { setData(null); return; }
    try { setData(await call('')); } catch (e) { setError(e.message); }
    // A team change shows straight away in the Team filters and columns (3.19.6)
    if (changed) refreshInfo?.(true);
  }, [call, allClients, client, refreshInfo]);
  useEffect(() => { load(); }, [load]);

  const teams = useMemo(() => data?.teams || [], [data]);
  const { sorted, sort, onSort } = useSort(teams, {
    name: t => t.name, number: t => String(t.number || '').replace(/\D/g, ''), members: t => t.members.length,
  }, { key: 'name', dir: 1 });

  async function save(team, f) {
    const body = JSON.stringify({ name: f.name, number: f.number, alsoClient: !!f.alsoClient, notify: { incoming: toList(f.incoming), incomingCc: toList(f.incomingCc), incomingBcc: toList(f.incomingBcc) } });
    if (team) await call(`/${team.id}`, { method: 'PUT', body });
    else await call('', { method: 'POST', body });
    notify({ tone: 'success', title: team ? `${f.name} saved` : `Team ${f.name} created` });
    setAdding(false);
    await load(true);
  }
  async function remove(team) {
    // 3.19.7: only a team with no members and no faxes can be deleted (the
    // server checks); with faxes, rename it instead
    if (team.members.length) {
      notify({ tone: 'error', title: `${team.name} can't be deleted while it has members`, detail: `Remove its ${team.members.length} member${team.members.length === 1 ? '' : 's'} from the team first. A team that has sent or received faxes can't be deleted; rename it instead.` });
      return;
    }
    if (!(await confirm({ title: `Delete ${team.name}?`, message: `Only a team with no members and no faxes can be deleted. Faxes to ${fmtFax(team.number)} are then seen by everyone at ${atCo}.`, confirmLabel: 'Delete team', danger: true }))) return;
    try { await call(`/${team.id}`, { method: 'DELETE' }); notify({ tone: 'success', title: `${team.name} deleted` }); setOpenId(null); await load(true); }
    catch (e) { notify({ tone: 'error', title: "Couldn't delete it", detail: e.message }); }
  }
  async function setMembers(team, emails) {
    try {
      const r = await call(`/${team.id}/members`, { method: 'PUT', body: JSON.stringify({ emails }) });
      if (r.added?.length || r.removed?.length) notify({ bell: 'people', tone: 'success', title: `${team.name}: ${[r.added?.length ? `${r.added.length} added` : '', r.removed?.length ? `${r.removed.length} removed` : ''].filter(Boolean).join(', ')}` });
      await load(true);
    } catch (e) { notify({ tone: 'error', title: "Couldn't change the members", detail: e.message }); }
  }
  async function makeDefault(person, team) {
    try { await call(`/people/${encodeURIComponent(person.email)}/default`, { method: 'PUT', body: JSON.stringify({ teamId: team.id }) }); await load(true); }
    catch (e) { notify({ tone: 'error', title: "Couldn't change it", detail: e.message }); }
  }

  return (
    <div>
      <h3 style={SECTION_TITLE}>Teams</h3>
      <p style={{ ...SECTION_SUB, marginBottom: 14 }}>
        Teams inside a business client. Each team has its own fax number (one team per number) and its members.
        A team's incoming faxes are seen by its members and {atCo}'s Admins; members send from their default team's number.
        A number no team uses is seen by everyone at {atCo}.
      </p>

      {allClients && (
        <label style={S.pick}>Client
          <select value={client} onChange={e => { setClient(e.target.value); setOpenId(null); setAdding(false); }} style={S.select}>
            <option value="">Pick a client…</option>
            {business.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
      )}
      {!allClients && current?.type === 'individual' ? <p style={S.muted}>Teams are for business clients. {current.name} is an individual client: everyone there sees all its faxes.</p> : <>
      {error && <div role="alert" style={S.error}>{error}</div>}
      {allClients && !client ? <p style={S.muted}>Pick a client to see its teams.</p> : !data ? (!error && <Loader label="Loading teams…" />) : (
        <>
          <div style={S.bar}>
            <span style={S.muted}>{data.account.name}: {teams.length} team{teams.length === 1 ? '' : 's'} · {data.people.length} people · numbers {data.account.numbers.map(n => fmtFax(n.number)).join(', ') || '—'}</span>
            {!adding && <button onClick={() => { setAdding(true); setOpenId(null); }} style={S.primaryBtn}>＋ New team</button>}
          </div>
          {/* 3.19.10: which team faxes from before teams count as, in the Fax
              Usage Report's split by team */}
          {/* 3.19.10: the default team: people added to the client join it */}
          {teams.length > 0 && (
            <label style={{ ...S.pick, fontWeight: 400 }}>
              <span style={{ fontWeight: 600 }}>Default team (new people join it)</span>
              <select value={data.account.defaultTeam || ''} style={S.select}
                onChange={async e => {
                  try { await call('/default-team', { method: 'PUT', body: JSON.stringify({ teamId: e.target.value }) }); notify({ tone: 'success', title: 'Default team saved' }); await load(true); }
                  catch (x) { notify({ tone: 'error', title: "Couldn't save it", detail: x.message }); }
                }}>
                {!data.account.defaultTeam && <option value="">Pick a team…</option>}
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}
          {teams.length > 0 && (
            <label style={{ ...S.pick, fontWeight: 400 }}>
              <span style={{ fontWeight: 600 }}>Faxes from before teams count as</span>
              <select value={data.account.historyTeam || ''} style={S.select}
                onChange={async e => {
                  try { await call('/history', { method: 'PUT', body: JSON.stringify({ teamId: e.target.value }) }); notify({ tone: 'success', title: 'Saved' }); await load(); }
                  catch (x) { notify({ tone: 'error', title: "Couldn't save it", detail: x.message }); }
                }}>
                <option value="">No team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <span style={S.small}>Used by Usage → Split by team for months before {atCo} had teams.</span>
            </label>
          )}
          {adding && <TeamForm numbers={data.account.numbers} teams={teams} onCancel={() => setAdding(false)} onSave={f => save(null, f)} />}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr style={S.thead}>
                <SortTh k="name" label="Team" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="number" label="Fax number" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="members" label="Members" sort={sort} onSort={onSort} style={{ ...S.th, textAlign: 'right' }} />
                <th style={S.th}>Received faxes emailed to</th>
                <th style={S.th}><span style={S.srOnly}>Details</span></th>
              </tr></thead>
              <tbody>
                {!sorted.length ? <tr><td colSpan={5} style={S.empty}>No teams yet. Everyone at {data.account.name} sees all its incoming faxes.</td></tr>
                  : sorted.map((t, i) => {
                    const open = openId === t.id;
                    const shared = teams.filter(x => x.id !== t.id && String(x.number).slice(-10) === String(t.number).slice(-10));
                    return [
                      <tr key={t.id} onClick={() => setOpenId(open ? null : t.id)} aria-expanded={open} style={{ cursor: 'pointer', background: open ? 'var(--c-e0f2f1)' : i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                        <td style={S.td}><strong>{t.name}</strong>{t.id === data.account.defaultTeam && <span style={{ ...S.badge, marginLeft: 8 }}>Default</span>}</td>
                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{fmtFax(t.number)}{shared.length > 0 && <div style={S.small}>shared with {shared.map(x => x.name).join(', ')}</div>}</td>
                        <td style={{ ...S.td, textAlign: 'right' }}>{t.members.length}</td>
                        <td style={S.td}>{toText(t.notify?.incoming) ? <>{toText(t.notify.incoming)}{t.alsoClient && <div style={S.small}>+ {atCo}'s recipients</div>}</> : <span style={S.small}>{atCo === 'the client' ? 'The client' : 'Your company'}'s ({toText(data.account.notify?.incoming) || 'not set'})</span>}</td>
                        <td style={{ ...S.td, textAlign: 'right', color: 'var(--t-00695c)', whiteSpace: 'nowrap' }}>{open ? '▲ Hide' : '▼ Details'}</td>
                      </tr>,
                      open && (
                        <tr key={`${t.id}:d`}><td colSpan={5} style={S.detail}>
                          <TeamForm team={t} numbers={data.account.numbers} teams={teams} onCancel={() => setOpenId(null)} onSave={f => save(t, f)}
                            onDelete={t.id === data.account.defaultTeam ? null : () => remove(t)} />
                          {t.id === data.account.defaultTeam && <p style={S.small}>This is the default team, so it can't be deleted. Make another team the default first.</p>}
                          <Members team={t} people={data.people} teams={teams} onChange={emails => setMembers(t, emails)} onDefault={makeDefault} />
                        </td></tr>
                      ),
                    ];
                  })}
              </tbody>
            </table>
          </div>
        </>
      )}
      </>}
    </div>
  );
}

function TeamForm({ team = null, numbers, teams = [], onCancel, onSave, onDelete }) {
  const atCo = useClientWord() === 'Client' ? 'the client' : 'your company';   // 3.67.5
  // Numbers another team already has (one team per number, 3.19.6)
  const takenBy = n => teams.find(t => t.id !== team?.id && String(t.number).replace(/\D/g, '').slice(-10) === String(n).replace(/\D/g, '').slice(-10));
  const free = numbers.filter(n => !takenBy(n.number));
  const [f, setF] = useState(() => team
    ? { name: team.name, number: team.number, incoming: toText(team.notify?.incoming), incomingCc: toText(team.notify?.incomingCc), incomingBcc: toText(team.notify?.incomingBcc), alsoClient: !!team.alsoClient }
    : { ...EMPTY, number: free[0]?.number || '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const hasOwn = [f.incoming, f.incomingCc, f.incomingBcc].some(v => toList(v).length > 0);   // 3.59.3
  // 3.20.3: say why it can't be created instead of a silently disabled button
  const noNumber = !team && !free.length;
  const NO_NUMBER = `No fax number is available for a new team: every fax number of ${atCo === 'the client' ? 'this client' : 'your company'} already belongs to a team, and a fax number can belong to only one team. To set up another team, please contact Fax Portal support to get a new fax number.`;
  async function submit(e) {
    e.preventDefault(); setErr('');
    const problem = !f.name.trim() ? 'Enter a team name.'
      : noNumber ? "The team can't be created yet: no fax number is available. Contact Fax Portal support to get a new fax number."
      : !f.number ? 'Select a fax number for the team.'
      : takenBy(f.number) ? `${fmtFax(f.number)} already belongs to the ${takenBy(f.number).name} team. Pick another number, or contact Fax Portal support to get a new fax number.`
      : '';
    if (problem) { setErr(problem); return; }
    setBusy(true);
    try { await onSave(f); } catch (x) { setErr(x.message); }
    setBusy(false);
  }
  return (
    <form onSubmit={submit} style={S.form} onClick={e => e.stopPropagation()}>
      <div style={S.grid}>
        <label style={S.label}>Team name
          <input value={f.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Billing" style={S.input} />
        </label>
        <label style={S.label}><span>Fax number <span style={S.hint}>(one per team)</span></span>
          <select value={noNumber ? '' : f.number} onChange={e => set('number', e.target.value)} disabled={noNumber} style={{ ...S.input, ...(noNumber ? S.inputOff : {}) }}>
            {noNumber ? <option value="">No fax number available</option> : !f.number && <option value="">Select a fax number</option>}
            {!noNumber && numbers.map(n => { const by = takenBy(n.number); return (
              <option key={n.number} value={n.number} disabled={!!by}>{fmtFax(n.number)}{n.label ? ` (${n.label})` : ''}{by ? ` · ${by.name}'s` : ''}</option>
            ); })}
          </select>
        </label>
      </div>
      {noNumber && <div role="alert" style={S.error}>{NO_NUMBER}</div>}
      {/* 3.59.3 (owner): To, Cc or Bcc, any of them (Bcc only: nobody sees who else got it); 3.62.10: the hint follows the 3.59.2 team tick */}
      <label style={S.label}><span>Received faxes emailed to <span style={S.hint}>(optional, separate several with commas; with To, Cc and Bcc all empty, each person in the team gets their own email, or {atCo}'s list does if "Email each incoming fax to everyone in the team" is unticked in Settings → Fax Settings → Incoming Fax Emails)</span></span>
        <input value={f.incoming} onChange={e => set('incoming', e.target.value)} placeholder="team@example.com" style={S.input} />
      </label>
      <div style={S.grid}>
        <label style={S.label}><span>Cc <span style={S.hint}>(optional)</span></span>
          <input value={f.incomingCc} onChange={e => set('incomingCc', e.target.value)} style={S.input} />
        </label>
        <label style={S.label}><span>Bcc <span style={S.hint}>(optional)</span></span>
          <input value={f.incomingBcc} onChange={e => set('incomingBcc', e.target.value)} style={S.input} />
        </label>
      </div>
      {/* 3.19.1: optional; with the team's own recipients only the team gets
          the email unless this is ticked */}
      <label style={{ ...S.check, opacity: hasOwn ? 1 : 0.5 }}>
        <input type="checkbox" checked={!!f.alsoClient && hasOwn} disabled={!hasOwn}
          onChange={e => set('alsoClient', e.target.checked)} />
        Also send to {atCo}'s recipients (its To, Cc and Bcc)
        <span style={S.hint}>{hasOwn ? '' : ` (without the team's own recipients, ${atCo}'s get it anyway)`}</span>
      </label>
      {f.number && takenBy(f.number) && <div role="alert" style={S.error}>{fmtFax(f.number)} already belongs to the {takenBy(f.number).name} team. Pick another number, or contact Fax Portal support to get a new fax number.</div>}
      {err && <div role="alert" style={S.error}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="submit" disabled={busy} style={S.primaryBtn}>{busy ? 'Saving…' : team ? 'Save' : 'Create team'}</button>
        <button type="button" onClick={onCancel} style={S.ghostBtn}>Cancel</button>
        {onDelete && <button type="button" onClick={onDelete} style={{ ...S.ghostBtn, color: 'var(--t-b71c1c)', borderColor: 'var(--c-ef9a9a)', marginLeft: 'auto' }}>Delete team</button>}
      </div>
    </form>
  );
}

function Members({ team, people, teams, onChange, onDefault }) {
  const atCo = useClientWord() === 'Client' ? 'the client' : 'your company';   // 3.67.5
  const members = people.filter(p => team.members.includes(p.email));
  const [adding, setAdding] = useState(false);
  return (
    <div style={{ marginTop: 16 }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={S.subTitle}>Members ({members.length})</div>
        {!adding && <button onClick={() => setAdding(true)} style={S.primaryBtn}>＋ Add members</button>}
      </div>
      {adding && (
        <AddMembers team={team} people={people.filter(p => !team.members.includes(p.email))} teams={teams}
          onCancel={() => setAdding(false)}
          onAdd={async emails => { await onChange([...team.members, ...emails]); setAdding(false); }} />
      )}
      {!members.length ? <p style={S.small}>Nobody yet. Add people with ＋ Add members (they must be at {atCo === 'the client' ? 'this client' : 'your company'} first, in People).</p> : (
        <table style={S.table}><tbody>
          {members.map(p => {
            const isDefault = p.defaultTeam === team.id;
            const others = p.teams.filter(id => id !== team.id).map(id => teams.find(t => t.id === id)?.name).filter(Boolean);
            return (
              <tr key={p.email} style={{ borderBottom: '1px solid var(--c-eceff1)' }}>
                <td style={{ ...S.td, padding: '8px 4px' }}><strong>{p.name || p.email}</strong>{p.name && <span style={S.small}> {p.email}</span>}<div style={S.small}>{p.roleLabel}{others.length ? ` · also in ${others.join(', ')}` : ''}</div></td>
                <td style={{ ...S.td, padding: '8px 4px', whiteSpace: 'nowrap' }}>
                  {isDefault ? <span style={S.badge}>Default team</span>
                    : <button onClick={() => onDefault(p, team)} style={S.linkBtn} title="They send faxes by email from this team's number">Make default</button>}
                </td>
                <td style={{ ...S.td, padding: '8px 4px', textAlign: 'right' }}>
                  <button onClick={() => onChange(team.members.filter(e => e !== p.email))} style={S.linkBtn}>Remove</button>
                </td>
              </tr>
            );
          })}
        </tbody></table>
      )}
    </div>
  );
}

// Everyone at the client who isn't in the team yet, as a checklist with a
// search box and "tick all shown" (3.19.1: the type-ahead box only offered
// 50 people)
function AddMembers({ team, people, teams, onCancel, onAdd }) {
  const atCo = useClientWord() === 'Client' ? 'the client' : 'your company';   // 3.67.5
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const t = q.trim().toLowerCase();
  const shown = !t ? people : people.filter(p => [p.name, p.email, p.roleLabel].some(v => String(v || '').toLowerCase().includes(t)));
  const allShown = shown.length > 0 && shown.every(p => picked.has(p.email));
  const toggle = e => setPicked(s => { const n = new Set(s); if (n.has(e)) n.delete(e); else n.add(e); return n; });
  const toggleShown = () => setPicked(s => { const n = new Set(s); shown.forEach(p => (allShown ? n.delete(p.email) : n.add(p.email))); return n; });
  return (
    <div style={S.picker}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, email or role" style={{ ...S.input, flex: '1 1 240px', maxWidth: 360 }} autoFocus />
        <label style={S.check}><input type="checkbox" checked={allShown} onChange={toggleShown} disabled={!shown.length} /> Tick all {t ? 'shown' : ''} ({shown.length})</label>
        <span style={S.small}>{people.length} people at {atCo === 'the client' ? 'this client' : 'your company'} aren't in {team.name}</span>
      </div>
      <div style={S.pickList}>
        {!shown.length ? <p style={S.small}>{people.length ? 'Nobody matches.' : `Everyone at ${atCo === 'the client' ? 'this client' : 'your company'} is already in this team.`}</p> : shown.map(p => {
          const inTeams = p.teams.map(id => teams.find(x => x.id === id)?.name).filter(Boolean);
          return (
            <label key={p.email} style={{ ...S.pickRow, background: picked.has(p.email) ? 'var(--c-e0f2f1)' : 'transparent' }}>
              <input type="checkbox" checked={picked.has(p.email)} onChange={() => toggle(p.email)} />
              <span><strong>{p.name || p.email}</strong>{p.name && <span style={S.small}> {p.email}</span>}
                <span style={S.small}> · {p.roleLabel}{inTeams.length ? ` · in ${inTeams.join(', ')}` : ''}</span></span>
            </label>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button disabled={!picked.size || busy} onClick={async () => { setBusy(true); await onAdd([...picked]); setBusy(false); }}
          style={{ ...S.primaryBtn, opacity: picked.size ? 1 : 0.5 }}>{busy ? 'Adding…' : `Add ${picked.size || ''} to ${team.name}`}</button>
        <button onClick={onCancel} style={S.ghostBtn}>Cancel</button>
      </div>
    </div>
  );
}

const S = {
  muted: { color: 'var(--t-78909c)', fontSize: 13, margin: '6px 0' },
  small: { fontSize: 11, color: 'var(--t-78909c)', fontWeight: 400 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13, margin: '8px 0' },
  pick: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600, marginBottom: 12 },
  select: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)', minWidth: 220 },
  bar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '4px 0 12px' },
  primaryBtn: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  ghostBtn: { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-00695c)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: 0 },
  badge: { fontSize: 11, fontWeight: 700, background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', padding: '2px 8px', borderRadius: 20 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle', borderBottom: '1px solid var(--c-f0f0f0)' },
  empty: { textAlign: 'center', padding: '30px 0', color: 'var(--t-78909c)', fontSize: 13 },
  detail: { padding: '14px 18px 18px', background: 'var(--c-f7fbfa)', borderBottom: '1px solid var(--c-e0f2f1)' },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  form: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 14px' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', margin: '6px 0' },
  hint: { fontWeight: 400, color: 'var(--t-90a4ae)' },
  input: { padding: '8px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit', fontWeight: 400, color: 'var(--t-333333)' },
  inputOff: { background: 'var(--c-f5f5f5)', color: 'var(--t-90a4ae)', cursor: 'not-allowed' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--t-37474f)', margin: '8px 0 2px' },
  picker: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '12px 14px', marginBottom: 12 },
  pickList: { maxHeight: 320, overflowY: 'auto', border: '1px solid var(--c-eceff1)', borderRadius: 8, padding: 4 },
  pickRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  subTitle: { fontSize: 13, fontWeight: 700, color: 'var(--t-00695c)' },
};
